import 'react-dropdown/style.css';
import { useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { styled } from '@mui/material/styles';
import { useFloorContext } from '../../context/FloorContext';
import { useSearchContext } from '../../context/SearchContext';

type Option = {
  value: string | number;
  label: string;
};

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: 2,
    userSelect: 'none',
  },
  '& .MuiInputBase-input': {
    position: 'relative',
    borderRadius: 4,
    border: '1px solid #407899',
    fontSize: 16,
    padding: '6px 12px 6px 12px',
  },
  '& .MuiSelect-icon': {
    color: '#fff',
  },
}));

const ClearFilterIcon = ({
  selected,
  onSelect,
}: {
  selected: string | number;
  onSelect: (obj: any) => void;
}) => {
  return (
    <IconButton
      sx={{
        visibility:
          selected !== '' && selected.toString() !== 'NaN'
            ? 'visible'
            : 'hidden',
        position: 'absolute',
        right: -4,
        background: '#407899 !important',
        border: '1px solid #407899',
        padding: '5px 7px 5px 7px',
        borderRadius: '0px 8px 8px 0px',
      }}
      onClick={() => onSelect({ target: { value: '' } })}
    >
      <ClearIcon />
    </IconButton>
  );
};

const labelStyle = {
  color: '#fff !important',
  background: '#0d334a',
  padding: '2px 4px 2px 4px',
};

const Filters = () => {
  const { filters, setFilters } = useSearchContext();
  const { totalFloors } = useFloorContext();

  const findOptionByValue = (options: Option[], value?: string | number) => {
    if (value === undefined) return '';

    return (
      options.find(
        (opt) =>
          opt.value === value ||
          opt.value.toString() === value ||
          opt.value === value.toString()
      )?.value || ''
    );
  };

  const lockStateOptions: Option[] = [
    { value: 'open', label: 'Open' },
    { value: 'friends', label: 'Friends only' },
    { value: 'closed', label: 'Closed' },
  ];
  const [selectedLockState, setSelectedLockState] = useState<string>(
    () => findOptionByValue(lockStateOptions, filters.lockedState) as string
  );
  const onSelectLockState = (e: SelectChangeEvent) => {
    setSelectedLockState(e.target.value as string);

    if (e.target.value === '') {
      setFilters({
        ...filters,
        lockedState: undefined,
      });
    } else {
      setFilters({
        ...filters,
        lockedState: e.target.value as 'open' | 'closed' | 'friends',
      });
    }
  };

  const visitorsLimitOptions: Option[] = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 75, label: '75' },
  ];
  const [selectedVisitorsLimit, setSelectedVisitorsLimit] = useState<number>(
    () =>
      parseInt(
        findOptionByValue(visitorsLimitOptions, filters.visitorLimit) as string
      )
  );

  const onSelectVisitorsLimit = (e: SelectChangeEvent) => {
    setSelectedVisitorsLimit(parseInt(e.target.value));
    if (e.target.value === '') {
      setFilters({
        ...filters,
        visitorLimit: undefined,
      });
    } else {
      setFilters({
        ...filters,
        visitorLimit: parseInt(e.target.value),
      });
    }
  };

  const sizeOptions: Option[] = [
    { value: '1x1', label: '1x1' },
    { value: '2x1', label: '2x1' },
    { value: '2x2', label: '2x2' },
    { value: '3x2', label: '3x2' },
    { value: '3x3', label: '3x3' },
    { value: '4x4', label: '4x4' },
  ];
  const [selectedSize, setSelectedSize] = useState<string>(
    () => findOptionByValue(sizeOptions, filters.size) as string
  );

  const onSelectSize = (e: SelectChangeEvent) => {
    setSelectedSize(e.target.value as string);
    if (e.target.value === '') {
      setFilters({
        ...filters,
        size: undefined,
      });
    } else {
      setFilters({
        ...filters,
        size: e.target.value,
      });
    }
  };

  const floorOptions: Option[] = [
    ...Array(totalFloors)
      .fill(0)
      .map((x, i) => ({ value: `${i + 1}`, label: `Floor ${i + 1}` })),
  ];
  const [selectedFloor, setSelectedFloor] = useState<string>(
    () => findOptionByValue(floorOptions, filters.floorNumber) as string
  );

  const onSelectFloor = (e: SelectChangeEvent) => {
    setSelectedFloor(e.target.value as string);
    if (e.target.value === '') {
      setFilters({
        ...filters,
        floorNumber: undefined,
      });
    } else {
      setFilters({
        ...filters,
        floorNumber: parseInt(e.target.value),
      });
    }
  };

  return (
    <div className="filter-ui-container">
      <Box component="div" sx={{ minWidth: 200 }}>
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel sx={labelStyle}>Privacy</InputLabel>
          <Select
            value={selectedLockState}
            onChange={onSelectLockState}
            className="dropdown-select"
            input={<BootstrapInput />}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: selectedLockState !== '' ? 'none' : '',
              },
              '.MuiIconButton-root': { color: '#fff' },
            }}
            endAdornment={
              <ClearFilterIcon
                selected={selectedLockState}
                onSelect={onSelectLockState}
              />
            }
          >
            {lockStateOptions.map((ls) => (
              <MenuItem key={ls.value} value={ls.value}>
                {ls.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box component="div" sx={{ minWidth: 150 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyle}>Visitors</InputLabel>
          <Select
            value={
              isNaN(selectedVisitorsLimit)
                ? ''
                : selectedVisitorsLimit.toString()
            }
            onChange={onSelectVisitorsLimit}
            className="dropdown-select"
            input={<BootstrapInput />}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: isNaN(selectedVisitorsLimit) ? '' : 'none',
              },
              '.MuiIconButton-root': { color: '#fff' },
            }}
            endAdornment={
              <ClearFilterIcon
                selected={selectedVisitorsLimit.toString()}
                onSelect={onSelectVisitorsLimit}
              />
            }
          >
            {visitorsLimitOptions.map((vl) => (
              <MenuItem key={vl.value} value={vl.value}>
                {vl.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box component="div" sx={{ minWidth: 170 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyle}>Size</InputLabel>
          <Select
            value={selectedSize}
            onChange={onSelectSize}
            className="dropdown-select"
            input={<BootstrapInput />}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: selectedSize !== '' ? 'none' : '',
              },
              '.MuiIconButton-root': { color: '#fff' },
            }}
            endAdornment={
              <ClearFilterIcon
                selected={selectedSize}
                onSelect={onSelectSize}
              />
            }
          >
            {sizeOptions.map((size) => (
              <MenuItem key={size.value} value={size.value}>
                {size.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box component="div" sx={{ minWidth: 150 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={labelStyle}>Floor</InputLabel>
          <Select
            value={selectedFloor}
            onChange={onSelectFloor}
            className="dropdown-select"
            input={<BootstrapInput />}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: selectedFloor !== '' ? 'none' : '',
              },
              '.MuiIconButton-root': { color: '#fff' },
            }}
            endAdornment={
              <ClearFilterIcon
                selected={selectedFloor}
                onSelect={onSelectFloor}
              />
            }
          >
            {floorOptions.map((floor) => (
              <MenuItem key={'filter-floor-' + floor.value} value={floor.value}>
                {floor.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </div>
  );
};

export default Filters;
